import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback: FallbackComponent, componentName = 'Component' } = this.props;
      
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }

      return (
        <div style={{ 
          padding: 20, 
          background: '#fef2f2', 
          border: '1px solid #fca5a5', 
          borderRadius: 8, 
          margin: 10,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
          <h3 style={{ color: '#dc2626', marginBottom: 8, fontSize: 16 }}>
            ⚠️ {componentName} Error
          </h3>
          <p style={{ color: '#7f1d1d', fontSize: 14, marginBottom: 8 }}>
            Something went wrong with this component.
          </p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ 
                background: '#fff', 
                padding: 8, 
                fontSize: 11, 
                overflow: 'auto', 
                marginTop: 4,
                border: '1px solid #e5e7eb',
                borderRadius: 4
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              fontSize: 12,
              cursor: 'pointer',
              marginTop: 8
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specific error boundary components for each section
export const HeaderErrorBoundary = ({ children }) => (
  <ErrorBoundary componentName="Header" fallback={HeaderErrorFallback}>
    {children}
  </ErrorBoundary>
);

export const SidebarErrorBoundary = ({ children }) => (
  <ErrorBoundary componentName="Sidebar" fallback={SidebarErrorFallback}>
    {children}
  </ErrorBoundary>
);

export const MapErrorBoundary = ({ children }) => (
  <ErrorBoundary componentName="Map" fallback={MapErrorFallback}>
    {children}
  </ErrorBoundary>
);

// Fallback components
const HeaderErrorFallback = () => (
  <div style={{ 
    padding: '12px 24px', 
    background: '#fef2f2', 
    borderBottom: '1px solid #fca5a5',
    color: '#dc2626',
    fontWeight: 600,
    fontSize: 14
  }}>
    ⚠️ Header failed to load - Please refresh the page
  </div>
);

const SidebarErrorFallback = () => (
  <div style={{ 
    width: 300, 
    padding: 20, 
    background: '#fef2f2', 
    border: '1px solid #fca5a5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: '#dc2626'
  }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>Sidebar Error</div>
    <div style={{ fontSize: 12, textAlign: 'center' }}>
      The sidebar component failed to load. Map functionality may still work.
    </div>
  </div>
);

const MapErrorFallback = () => (
  <div style={{ 
    flex: 1, 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    flexDirection: 'column',
    background: '#fef2f2',
    color: '#dc2626'
  }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>
    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 18 }}>Map Failed to Load</div>
    <div style={{ fontSize: 14, textAlign: 'center', maxWidth: 300 }}>
      The map component encountered an error. Please check your internet connection and try refreshing the page.
    </div>
  </div>
);

export default ErrorBoundary;